/**
 * TTL-based sessionStorage cache utility.
 *
 * - Scoped per browser tab (sessionStorage) — each tab has its own cache.
 * - Automatically expires entries after TTL.
 * - Safe: silently no-ops if sessionStorage is unavailable or full.
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Read cached value. Returns null if missing or expired.
 */
export function cacheGet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() > entry.expiry) {
      sessionStorage.removeItem(key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Write value to cache with TTL (default 5 min).
 */
export function cacheSet(key, data, ttl = DEFAULT_TTL) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ data, expiry: Date.now() + ttl })
    );
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

/**
 * Remove a specific cache entry.
 */
export function cacheRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Remove all cache entries whose key starts with the given prefix.
 * Useful for invalidating all product caches on logout / idw change.
 */
export function cacheInvalidatePrefix(prefix) {
  try {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}
